import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import useHttpNoAuth from "../../hooks/use-http-no-auth";
import PhotoSection from "./PhotoSection";
import "./CheckinForm.css";
import "./RoomForm.css";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const SAVE_DEBOUNCE_MS = 800;

const formatTime = (date) =>
  date.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });

const KeysForm = () => {
  const { cfid } = useParams();
  const navigate = useNavigate();

  const { sendRequest: getCheckinRequest, isLoading, error } = useHttpNoAuth();
  const {
    sendRequest: saveKeysRequest,
    isLoading: isSaving,
    error: saveError,
  } = useHttpNoAuth();
  const {
    sendRequest: uploadPhotosRequest,
    isLoading: isUploading,
    error: uploadError,
  } = useHttpNoAuth();
  const {
    sendRequest: deletePhotoRequest,
    isLoading: isDeletingPhoto,
    error: deletePhotoError,
  } = useHttpNoAuth();

  const [address, setAddress] = useState("");
  // Postal code and city read as one place, the way the check-in header
  // writes them.
  const [place, setPlace] = useState("");
  const [loaded, setLoaded] = useState(false);
  // Signed off, the handover is part of the record — everything here only reads.
  const [isSignedOff, setIsSignedOff] = useState(false);
  // The same page serves both handovers — every label follows the type the API
  // sends, and anything that is not a check-out is a check-in.
  const [isCheckout, setIsCheckout] = useState(false);
  const typeLabel = isCheckout ? "check-out" : "check-in";
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [savedAt, setSavedAt] = useState(null);
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  // How many thumbs to hold open while the upload is in flight.
  const [uploadingCount, setUploadingCount] = useState(0);

  // What the server currently holds — nothing is sent while the form matches it.
  const baselineRef = useRef(null);

  const loadCheckin = useCallback(() => {
    if (!cfid) return;
    getCheckinRequest(
      { url: `${BACKEND_URL}/welocate/checkins/${cfid}`, method: "GET" },
      (data) => {
        setAddress(
          [data?.street, data?.number, data?.floor].filter(Boolean).join(" ")
        );
        setPlace([data?.postal_code, data?.city].filter(Boolean).join(" "));
        setIsSignedOff(Boolean(data?.is_signed_off));
        setIsCheckout(data?.type === "checkout");

        const fetched = data?.keys_comments ?? "";
        setComment(fetched);
        baselineRef.current = fetched;
        // The check-in keeps the keys photos apart from the room ones.
        setPhotos(data?.keys_photos ?? []);
        setLoaded(true);
      }
    );
  }, [cfid, getCheckinRequest]);

  useEffect(() => {
    loadCheckin();
  }, [loadCheckin]);

  // Recomputed every render: the baseline lives in a ref and moves on each save.
  const base = baselineRef.current;
  const isDirty = base !== null && base !== comment;

  const saveKeys = useCallback(() => {
    const sent = comment;
    return saveKeysRequest(
      {
        url: `${BACKEND_URL}/welocate/checkins/${cfid}`,
        method: "PATCH",
        body: { keys_comments: sent },
      },
      () => {
        baselineRef.current = sent;
        setSavedAt(new Date());
      }
    );
  }, [comment, cfid, saveKeysRequest]);

  // Autosave: the design has no save button for the field, only a "done" exit.
  useEffect(() => {
    if (!isDirty || isSignedOff) return undefined;
    const timer = setTimeout(saveKeys, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [isDirty, isSignedOff, saveKeys]);

  const uploadPhotos = (fileList) => {
    const files = Array.from(fileList ?? []);
    if (files.length === 0 || isSignedOff) return;

    const formData = new FormData();
    formData.append("checkin_id", cfid);
    files.forEach((file) => formData.append("files", file));

    setUploadingCount(files.length);
    uploadPhotosRequest(
      {
        url: `${BACKEND_URL}/welocate/checkins/${cfid}/keys-photos`,
        method: "POST",
        body: formData,
        isFileUpload: true,
      },
      // The created photos come back from the POST, so the new thumbs are added
      // from there rather than by refetching — a refetch would drop a comment
      // typed while the upload was still running.
      (created) => {
        setPhotos((current) => [...current, ...(created ?? [])]);
        setUploadingCount(0);
        setSavedAt(new Date());
      }
    );
  };

  // The hook never rejects, so a failed upload has to clear the skeletons here.
  useEffect(() => {
    if (uploadError) setUploadingCount(0);
  }, [uploadError]);

  const deletePhoto = (photoId) => {
    if (isDeletingPhoto || isSignedOff) return;
    setDeletingPhotoId(photoId);
    deletePhotoRequest(
      { url: `${BACKEND_URL}/welocate/photos/${photoId}`, method: "DELETE" },
      () => {
        setDeletingPhotoId(null);
        setPhotos((current) => current.filter((photo) => photo.id !== photoId));
        setSavedAt(new Date());
      }
    );
  };

  // The hook never rejects, so a failed delete has to release the thumb here.
  useEffect(() => {
    if (deletePhotoError) setDeletingPhotoId(null);
  }, [deletePhotoError]);

  const finish = async () => {
    if (isDirty) await saveKeys();
    navigate(`/checkin/${cfid}`);
  };

  const isBusy = isSaving || isUploading;
  const hasFailed = !isBusy && Boolean(saveError || uploadError);

  // Edits are only in the browser until the debounce fires and the PATCH lands,
  // so the way out stays shut until then. A failed save reopens it — the error
  // is on screen and stranding the user on the page helps nobody.
  const isSavePending = (isDirty || isSaving) && !saveError;

  return (
    <div className="cf-page">
      <header className="cf-topbar">
        <button
          type="button"
          className="cf-back"
          aria-label={isSavePending ? "Saving — please wait" : "Back"}
          aria-busy={isSavePending}
          disabled={isSavePending}
          onClick={() => navigate(`/checkin/${cfid}`)}
        >
          {isSavePending ? <span className="cf-spinner" /> : <ChevronLeftIcon />}
        </button>

        <div className="cf-topbar-titles">
          <h1 className="cf-topbar-title">Keys</h1>
          {/* A record missing either part keeps what it has on its own,
              rather than trailing a separator with nothing after it. */}
          <p className="cf-topbar-sub">
            {[address, place].filter(Boolean).join(" · ")}
          </p>
        </div>

        {(isBusy || hasFailed || savedAt) && (
          <span
            className={`cf-saved${hasFailed ? " rf-saved--error" : ""}`}
            role="status"
          >
            {isBusy && "Saving…"}
            {hasFailed && "Not saved"}
            {!isBusy && !hasFailed && (
              <>
                <CheckCircleOutlineIcon />
                Saved <span className="cf-saved-time">{formatTime(savedAt)}</span>
              </>
            )}
          </span>
        )}
      </header>

      {isLoading && !loaded && <p className="cf-state">Loading keys…</p>}

      {!isLoading && error && (
        <div className="cf-state cf-state--error">
          <p className="cf-state-text">{error}</p>
          <button type="button" className="cf-state-retry" onClick={loadCheckin}>
            Try again
          </button>
        </div>
      )}

      {loaded && (
        <main className="cf-main">
          {isSignedOff && (
            <p className="cf-locked" role="status">
              <LockOutlinedIcon />
              This {typeLabel} has been signed off. Nothing here can be changed.
            </p>
          )}

          <section className="cf-card rf-block">
            <label className="rf-label" htmlFor="kf-comment">
              Keys
            </label>
            <textarea
              id="kf-comment"
              className="rf-notes"
              placeholder="e.g. 2 front-door keys, 1 mailbox key, 1 storage key handed over…"
              value={comment}
              disabled={isSignedOff}
              onChange={(event) => setComment(event.target.value)}
            />
            {!isSignedOff && (
              <p className="rf-help">
                Sets handed over, labels, anything missing or promised later.
              </p>
            )}
          </section>

          <section className="cf-card rf-block">
            <PhotoSection
              title="KEYS — PHOTOS"
              cameraLabel="Photograph"
              photos={photos}
              pendingCount={uploadingCount}
              alt="Keys"
              deleteLabel="Delete keys photo"
              isUploading={isUploading}
              onPick={uploadPhotos}
              onDelete={deletePhoto}
              deletingPhotoId={deletingPhotoId}
              isDeletingPhoto={isDeletingPhoto}
              isLocked={isSignedOff}
              errors={[
                !isUploading && uploadError,
                !isDeletingPhoto && deletePhotoError,
              ].filter(Boolean)}
            />
          </section>

          {!isSaving && saveError && (
            <p className="rf-error" role="alert">
              {saveError}
            </p>
          )}
        </main>
      )}

      {loaded && (
        <footer className="cf-actions">
          <div className="cf-actions-inner">
            <button
              type="button"
              className="cf-btn cf-btn--primary"
              disabled={isSaving}
              onClick={finish}
            >
              <CheckIcon />
              {isSignedOff ? `Back to ${typeLabel}` : "Done with keys"}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
};

export default KeysForm;
